import type { MaturitySignal, SignalReading } from '@bloo-team/core-shell'
import { useWorkbooks } from '../lib/detections'
import { useCoverageTotals } from '../lib/coverage'
import { useSignals } from '../lib/signals'
import { useSuppressionRules } from '../lib/suppression'
import { useCmCases, useCmStats } from '../lib/case-mgmt'
import { useHunts } from '../lib/hunt-store'
import {
  attackCoverage,
  caseOwnership,
  caseResponseKnown,
  casesWorked,
  detectionsEnabled,
  detectionsFiring,
  huntingCadence,
  signalsFlowing,
  tuningInUse,
} from './signal-data'

/**
 * Vantage's deployment-journey signals — phase Detect, and the security half
 * of Operate (`docs/home-journey-requirements.md` §5, S4).
 *
 * Every read here is one a Vantage page already makes, and every one of them
 * is CHEAP. That rules out the obvious source for a few of these: the
 * Detections page counts enabled and silent rules by joining `attack/analytics`
 * (~2.4 MB live), which Home rejected for exactly this reason
 * (`docs/overview.md` §Skipped by design). The workbook list carries
 * `disabled`, `signal_count` and `signal_last_seen` directly, so the same two
 * questions are answered from a list the Detections page already loads.
 */

const WINDOW = '1d' as const

/**
 * What a signal reads while its queries are in flight or after they failed.
 * Never a value — a zero standing in for a read that did not land says "you
 * have not done this", which is a different and wrong statement (J7).
 */
function pending(reads: Array<{ isPending: boolean; isError: boolean }>): SignalReading | null {
  if (reads.some((r) => r.isError)) return { state: 'error', value: null }
  if (reads.some((r) => r.isPending)) return { state: 'loading', value: null }
  return null
}

/** "Now" is the READ's time, never `Date.now()` in render (`docs/home.md`). */
function readAt(...reads: Array<{ dataUpdatedAt: number }>): number | null {
  const stamps = reads.map((r) => r.dataUpdatedAt).filter((t) => t > 0)
  return stamps.length === reads.length ? Math.max(...stamps) : null
}

const LOADING: SignalReading = { state: 'loading', value: null }

type Scope = { clusterId: string; scopeId: string }

function useDetectionsEnabled({ clusterId, scopeId }: Scope): SignalReading {
  const workbooks = useWorkbooks({ clusterId, scopeId })
  return pending([workbooks]) ?? detectionsEnabled(workbooks.data ?? [])
}

function useDetectionsFiring({ clusterId, scopeId }: Scope): SignalReading {
  const workbooks = useWorkbooks({ clusterId, scopeId })
  const blocked = pending([workbooks])
  const now = readAt(workbooks)
  if (blocked) return blocked
  if (now === null) return LOADING
  return detectionsFiring(workbooks.data ?? [], now)
}

function useAttackCoverage({ clusterId, scopeId }: Scope): SignalReading {
  const totals = useCoverageTotals({ clusterId, scopeId })
  return pending([totals]) ?? attackCoverage(totals.data)
}

function useSignalsFlowing({ clusterId, scopeId }: Scope): SignalReading {
  const signals = useSignals({ clusterId, scopeId }, WINDOW)
  return pending([signals]) ?? signalsFlowing(signals.signals.length)
}

function useTuningInUse({ clusterId, scopeId }: Scope): SignalReading {
  const rules = useSuppressionRules({ clusterId, scopeId })
  return pending([rules]) ?? tuningInUse(rules.data ?? [])
}

/** Both case checks read the same 90-day case-metrics call (one request, shared by its query key). */
const JOURNEY_CASE_WINDOW = '90d'

function useCasesWorked({ clusterId, scopeId }: Scope): SignalReading {
  const stats = useCmStats({ clusterId, scopeId }, JOURNEY_CASE_WINDOW)
  return pending([stats]) ?? casesWorked(stats.data?.counts)
}

function useCaseResponse({ clusterId, scopeId }: Scope): SignalReading {
  const stats = useCmStats({ clusterId, scopeId }, JOURNEY_CASE_WINDOW)
  return pending([stats]) ?? caseResponseKnown(stats.data ? { mtta: stats.data.mtta?.mean ?? null, mttr: stats.data.mttr?.mean ?? null } : undefined)
}

function useCaseOwnership({ clusterId, scopeId }: Scope): SignalReading {
  const open = useCmCases({ clusterId, scopeId }, { page: 1, pageSize: 200, category: ['new', 'in_progress', 'on_hold'] })
  return pending([open]) ?? caseOwnership(open.data?.items ?? [])
}

function useHuntingCadence({ clusterId, scopeId }: Scope): SignalReading {
  // Hunts live in this browser (lib/hunt-store.ts), so there is no read to
  // wait on and no failure to report.
  const { hunts } = useHunts(clusterId, scopeId)
  return huntingCadence(hunts)
}

/**
 * Weights: 3 where the phase means nothing without it — a deployment with no
 * detections is not "detecting", and cases nobody closes are not a process.
 *
 * Two signals the requirements list are NOT here, and both for the same
 * reason: their read is too expensive for a landing page. Coverage TREND
 * needs a `CoverageSnapshot` built from the full matrix plus `attack/analytics`
 * (~2.4 MB), and the disposition-based half of tuning needs 30 days of
 * signals. Suppression rules answer "is tuning happening" from a list that
 * costs nothing, which is the part the journey can stand behind.
 */
export const vantageMaturitySignals: MaturitySignal[] = [
  { id: 'vantage.detections-enabled', phase: 'detect', title: 'Detections enabled', weight: 3, read: useDetectionsEnabled },
  { id: 'vantage.coverage', phase: 'detect', title: 'ATT&CK coverage', weight: 3, read: useAttackCoverage },
  { id: 'vantage.signals-flowing', phase: 'detect', title: 'Signal arriving', weight: 2, read: useSignalsFlowing },
  { id: 'vantage.detections-firing', phase: 'detect', title: 'Detections firing', weight: 2, read: useDetectionsFiring },
  { id: 'vantage.tuning', phase: 'detect', title: 'Tuning in use', weight: 2, read: useTuningInUse },
  { id: 'vantage.cases-worked', phase: 'operate', title: 'Cases worked', weight: 3, read: useCasesWorked },
  { id: 'vantage.case-ownership', phase: 'operate', title: 'Cases owned', weight: 2, read: useCaseOwnership },
  { id: 'vantage.case-response', phase: 'operate', title: 'Response time known', weight: 2, read: useCaseResponse },
  { id: 'vantage.hunts', phase: 'operate', title: 'Hunting cadence', weight: 1, read: useHuntingCadence },
]
